import { useCallback, useEffect, useRef, useState } from "react"

import * as fabric from "fabric"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { Image } from "@/features/studio-ai/types/studio-ai.types"

import { editImage } from "@/api/studio-ai-api"
import { ArrowLeft, Download } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable"
import { Separator } from "@/components/ui/separator"
import { useSelectedImageModel } from "@/features/studio-ai/hooks/use-studio-image-models"
import { useStudioImageThumbnail } from "@/features/studio-ai/hooks/use-studio-image-thumbnail"
import { MediaStatus, MediaType } from "@/features/studio-ai/types/studio-ai.types"
import { getApiErrorMessage } from "@/lib/api-error-message"
import { useStudioAIStore } from "@/stores/studio-ai-store"
import { KNOWN_SESSION_TYPES } from "@/types"

import { EditorCanvas } from "./editor-canvas"
import { EditorChatPanel } from "./editor-chat-panel"
import { EditorToolbar } from "./editor-toolbar"
import { exportMask } from "./editor-utils"
import { useEditorState } from "./use-editor-state"

function getClosestEditSize(
  width: number,
  height: number
): "1024x1024" | "1536x1024" | "1024x1536" {
  if (!width || !height) return "1024x1024"
  const ratio = width / height
  if (ratio > 1.2) return "1536x1024"
  if (ratio < 0.8) return "1024x1536"
  return "1024x1024"
}

export function ImageEditorView() {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.imageEditor" })
  const editorImageUrl = useStudioAIStore((state) => state.editorImageUrl)
  const editorImageJobId = useStudioAIStore((state) => state.editorImageJobId)
  const imageSettings = useStudioAIStore((state) => state.imageSettings)
  const currentProjectId = useStudioAIStore((state) => state.currentProjectId)
  const setCurrentView = useStudioAIStore((state) => state.setCurrentView)
  const setEditorImageUrl = useStudioAIStore((state) => state.setEditorImageUrl)
  const setEditorImageJobId = useStudioAIStore((state) => state.setEditorImageJobId)
  const editorSourceJobId = useStudioAIStore((state) => state.editorSourceJobId)
  const setEditorSourceJobId = useStudioAIStore((state) => state.setEditorSourceJobId)
  // setEditorImageJobId is also cleared on navigateAway alongside the URL
  // and source-id setters — keep the trio in sync so the next visit doesn't
  // get a stale job id when the URL is null.
  const addImage = useStudioAIStore((state) => state.addImage)
  const { capabilities, requestModelId } = useSelectedImageModel()
  const updateGenerationState = useStudioAIStore((state) => state.updateGenerationState)
  const resetGenerationState = useStudioAIStore((state) => state.resetGenerationState)

  // Track the image currently in the editor as (legacyUrl, jobId). The hook
  // resolves these through the batch-prefetch cache: when legacyUrl is an
  // /api/studio-ai/images/<id> path that <img>/fetch can't load without auth,
  // the hook swaps in a blob: URL that does. When legacyUrl is already
  // img-safe (data:/blob:), the hook short-circuits.
  const [currentLegacyUrl, setCurrentLegacyUrl] = useState(editorImageUrl ?? "")
  const [currentJobId, setCurrentJobId] = useState<string | undefined>(
    editorImageJobId ?? undefined
  )
  const resolved = useStudioImageThumbnail(currentJobId, currentLegacyUrl)
  // While the resolution is in flight (no blob yet, legacy isn't img-safe),
  // currentImageUrl is "" so the editor renders its "no image" placeholder.
  const currentImageUrl = resolved.url ?? ""
  const [isProcessing, setIsProcessing] = useState(false)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [originalDimensions, setOriginalDimensions] = useState<{
    width: number
    height: number
  } | null>(null)
  const canvasRef = useRef<fabric.Canvas | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const {
    activeTool,
    setActiveTool,
    brushSize,
    setBrushSize,
    maskOpacity,
    setMaskOpacity,
    canUndo,
    canRedo,
    pushUndo,
    undo,
    redo,
    pendingRestore,
    clearPendingRestore,
    resetStacks,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    messages,
    addMessage,
  } = useEditorState()

  const navigateAway = () => {
    setEditorImageUrl(null)
    setEditorImageJobId(null)
    setEditorSourceJobId(null)
    // Clear any in-flight progress card so the gallery / wizard the user
    // navigates to doesn't render leftover state from the editor session.
    resetGenerationState()
    setCurrentView("landing")
  }

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true)
    } else {
      navigateAway()
    }
  }

  const handleConfirmDiscard = () => {
    setShowDiscardDialog(false)
    navigateAway()
  }

  const handleUndo = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    undo(JSON.stringify(canvas.toJSON()))
  }

  const handleRedo = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    redo(JSON.stringify(canvas.toJSON()))
  }

  const handleClearMask = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Push current state to undo before clearing
    pushUndo(JSON.stringify(canvas.toJSON()))

    // Remove everything except FabricImage instances
    const toRemove = canvas.getObjects().filter((obj) => !(obj instanceof fabric.FabricImage))
    for (const obj of toRemove) {
      canvas.remove(obj)
    }
    canvas.renderAll()
  }

  const handleBeforeMaskChange = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    pushUndo(JSON.stringify(canvas.toJSON()))
  }, [pushUndo])

  const handleMaskChange = useCallback(() => {
    setHasUnsavedChanges(true)
  }, [setHasUnsavedChanges])

  const handleImageLoaded = useCallback((width: number, height: number) => {
    setOriginalDimensions({ width, height })
  }, [])

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const handleApply = useCallback(
    async (prompt: string) => {
      const canvas = canvasRef.current
      if (!canvas || !currentImageUrl) return

      // Add user message
      addMessage({ role: "user", content: prompt })
      setIsProcessing(true)
      let abortController: AbortController | null = null

      try {
        abortControllerRef.current?.abort()
        abortController = new AbortController()
        abortControllerRef.current = abortController
        updateGenerationState({
          isGenerating: true,
          status: "editing",
          progress: 0,
          partialImageUrl: null,
          currentJobId: null,
          estimatedTime: undefined,
        })

        // Use original image dimensions for mask and size calculation
        const imgW = originalDimensions?.width ?? canvas.width
        const imgH = originalDimensions?.height ?? canvas.height

        // Export mask from canvas at original resolution
        const maskBlob = exportMask(canvas, imgW, imgH)

        // Convert current image URL to File
        const imageResponse = await fetch(currentImageUrl, { signal: abortController.signal })
        const imageBlob = await imageResponse.blob()
        const imageType = imageBlob.type || "image/png"
        const imageExtension = imageType.includes("jpeg") ? "jpg" : "png"
        const imageFile = new File([imageBlob], `image.${imageExtension}`, { type: imageType })
        const editSize = getClosestEditSize(imgW, imgH)
        const outputFormat = imageSettings.outputFormat
        // Forward the real quality (the edit endpoint accepts the same levels as
        // generate), falling back when the chosen model doesn't offer it.
        const quality = capabilities.qualities.includes(imageSettings.quality)
          ? imageSettings.quality
          : capabilities.defaultQuality

        // Call the edit API. Stream callbacks push progress into the store so
        // any visible GenerationProgress renders the live preview.
        const result = await editImage({
          input_image: imageFile,
          prompt,
          mask: maskBlob ?? undefined,
          size: editSize,
          quality,
          output_format: outputFormat,
          project_id: currentProjectId || undefined,
          model_id: requestModelId,
          source_job_id: editorSourceJobId || undefined,
          signal: abortController.signal,
          onStart: () =>
            updateGenerationState({
              isGenerating: true,
              status: "editing",
              progress: 0,
              partialImageUrl: null,
              currentJobId: null,
              estimatedTime: undefined,
            }),
          onPartialImage: (imageUrl, progress) =>
            updateGenerationState({ progress, status: "editing", partialImageUrl: imageUrl }),
          onError: () => resetGenerationState(),
        })

        if (result.image_url) {
          updateGenerationState({
            isGenerating: false,
            status: "completed",
            progress: 100,
            partialImageUrl: result.image_url,
            currentJobId: result.job_id || null,
          })
          // Persist the edit result in the store/localStorage so it appears in the gallery
          const editedImage: Image = {
            id: result.job_id,
            job_id: result.job_id,
            project_id: currentProjectId || undefined,
            prompt,
            image_url: result.image_url,
            status: MediaStatus.Completed,
            size: editSize,
            quality,
            revised_prompt: result.revised_prompt,
            created_at: new Date().toISOString(),
            media_type: MediaType.Image,
            edit_mode: "edit",
            source_job_id: editorSourceJobId || undefined,
          }
          addImage(editedImage)

          // Add assistant message with result image. Pass the job id so the
          // chat panel resolves through the batch-prefetch cache instead of
          // letting <img src> hit /api/studio-ai/images/<id> directly.
          addMessage({
            role: "assistant",
            content: t("editApplied"),
            imageUrl: result.image_url,
            imageJobId: result.job_id || undefined,
          })

          // Replace canvas image and reset stacks (old JSON is now invalid).
          // result.image_url is typically /api/studio-ai/images/<new_id> — feed
          // it through the resolver hook (next render) by updating both the
          // job id and the legacy URL so we get a blob: URL we can actually
          // load. data:/blob: URLs from the streaming partial path skip the
          // resolver and render directly.
          setCurrentJobId(result.job_id || undefined)
          setCurrentLegacyUrl(result.image_url)

          // Update original dimensions to API output size
          const [w, h] = editSize.split("x").map((v) => Number(v))
          setOriginalDimensions({ width: w, height: h })

          resetStacks()
          // The edited result is already persisted to the gallery (addImage +
          // backend blob/Cosmos write), so leaving the editor loses nothing —
          // don't flag it as unsaved and nag the user to download. Drawing a
          // new mask stroke re-sets the flag via handleMaskChange.
          setHasUnsavedChanges(false)

          toast.success(t("editSuccess"))
        } else {
          addMessage({ role: "assistant", content: t("editFailed") })
          toast.error(t("editFailed"))
        }
      } catch (error) {
        if (
          error instanceof DOMException &&
          (error.name === "AbortError" || error.message.toLowerCase().includes("aborted"))
        ) {
          resetGenerationState()
          return
        }
        const msg = getApiErrorMessage(error, t("editFailed"))
        addMessage({ role: "assistant", content: msg })
        toast.error(t("editFailed"), { description: msg })
      } finally {
        if (abortController && abortControllerRef.current === abortController) {
          abortControllerRef.current = null
        }
        setIsProcessing(false)
      }
    },
    [
      currentImageUrl,
      addImage,
      addMessage,
      currentProjectId,
      editorSourceJobId,
      imageSettings.outputFormat,
      imageSettings.quality,
      capabilities,
      requestModelId,
      originalDimensions,
      t,
      updateGenerationState,
      resetGenerationState,
      resetStacks,
      setHasUnsavedChanges,
    ]
  )

  // The chat panel surfaces a thumbnail per assistant message; clicking one
  // restores that result into the main canvas. We get the legacy URL and
  // (optionally) the job id from the message so the resolver hook can
  // produce a blob: URL the canvas can actually load.
  const handleImageClick = useCallback((imageUrl: string, jobId: string | undefined) => {
    setCurrentJobId(jobId)
    setCurrentLegacyUrl(imageUrl)
  }, [])

  const handleDownload = useCallback(async () => {
    try {
      const response = await fetch(currentImageUrl)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `edited-image-${Date.now()}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setHasUnsavedChanges(false)
      toast.success(t("downloadSuccess"))
    } catch {
      toast.error(t("downloadFailed"))
    }
  }, [currentImageUrl, setHasUnsavedChanges, t])

  if (!currentImageUrl) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p className="text-muted-foreground">{t("noImage")}</p>
        <Button className="ml-4" variant="outline" onClick={handleBack}>
          {t("backToGallery")}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2">
        <Button size="sm" variant="ghost" onClick={handleBack}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t("back")}
        </Button>
        <h2 className="text-sm font-semibold">{t("title")}</h2>
        <div className="flex-1" />
        {hasUnsavedChanges && (
          <Button size="sm" variant="outline" onClick={handleDownload}>
            <Download className="mr-1 h-4 w-4" />
            {t("download")}
          </Button>
        )}
      </div>
      <Separator />

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Toolbar — fixed width, outside panel group */}
        <EditorToolbar
          activeTool={activeTool}
          brushSize={brushSize}
          canRedo={canRedo}
          canUndo={canUndo}
          maskOpacity={maskOpacity}
          onBrushSizeChange={setBrushSize}
          onClearMask={handleClearMask}
          onMaskOpacityChange={setMaskOpacity}
          onRedo={handleRedo}
          onToolChange={setActiveTool}
          onUndo={handleUndo}
        />

        {/* Resizable canvas + chat */}
        <ResizablePanelGroup direction="horizontal">
          <ResizablePanel defaultSize={75} minSize={40}>
            <EditorCanvas
              activeTool={activeTool}
              brushSize={brushSize}
              canvasRef={canvasRef}
              imageUrl={currentImageUrl}
              maskOpacity={maskOpacity}
              pendingRestore={pendingRestore}
              onBeforeMaskChange={handleBeforeMaskChange}
              onImageLoaded={handleImageLoaded}
              onMaskChange={handleMaskChange}
              onRestoreComplete={clearPendingRestore}
            />
          </ResizablePanel>

          <ResizableHandle withHandle />

          <ResizablePanel defaultSize={25} minSize={15}>
            <EditorChatPanel
              isProcessing={isProcessing}
              messages={messages}
              sessionType={KNOWN_SESSION_TYPES.Explorer}
              onApply={handleApply}
              onImageClick={handleImageClick}
            />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Discard unsaved changes dialog */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("unsavedChangesTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("unsavedChangesDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDiscard}>{t("discard")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
